/**
 * V167.5 — Cohort retrospective: shareable 5-day audit recap
 *
 * Public, read-only page rendered at /recap/:token. Even non-owners can open
 * it (it was made to be shared). Below the recap data we show a viral CTA
 * to join the next cohort — this is the leakage channel.
 *
 * Route: /recap/:token
 *
 * Data: GET /api/challenge/recap/:token?viewer=<optional-email>
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, CheckCircle2, ArrowRight, Share2, Eye, TrendingDown, ShieldAlert, Copy, AlertTriangle, Sparkles,
} from "lucide-react";
import { API } from "../config";

const CATEGORY = {
  visibility: { label: "Viditeľnosť (SEO / GEO)",       Icon: Eye,          color: "text-amber-300" },
  conversion: { label: "Konverzia (košík / dôvera)",    Icon: TrendingDown, color: "text-red-300" },
  ops:        { label: "Prevádzka (admin / GDPR)",      Icon: ShieldAlert,  color: "text-violet-300" },
};

const BUNDLE_TITLE = {
  visibility: "Bundle Viditeľnosť",
  conversion: "Bundle Konverzia",
  ops:        "Bundle Prevádzka",
};

export default function RecapPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Tag the request with viewer email if we know it (logged-in users only;
    // for unauth visitors we pass undefined and the backend marks as share-view).
    let viewer;
    try { viewer = JSON.parse(localStorage.getItem("user") || "null")?.email; } catch { /* noop */ }
    axios.get(`${API}/challenge/recap/${encodeURIComponent(token)}`, { params: viewer ? { viewer } : {} })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"));
  }, [token]);

  const copyLink = () => {
    const url = window.location.href;
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (err) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4"/>
          <h1 className="text-2xl font-black mb-2">Recap sa nenašiel</h1>
          <p className="text-white/60 text-sm mb-6">Tento share-link pravdepodobne expiroval alebo nikdy neexistoval.</p>
          <Link to="/vyzva-5-dni" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
            Zaregistruj sa do najbližšej výzvy <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400"/>
      </div>
    );
  }

  const rec = data.recommendation || {};
  const dominantBundle = rec.bundle_id || "conversion";
  const scores = rec.scores || {};

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-white font-black text-base tracking-tight">
          <span className="text-emerald-400">▲</span> TopRankPulse
        </Link>
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/40">
          5-Day Audit Recap
        </span>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10" data-testid="recap-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
            <Sparkles className="w-3 h-3"/> Kompletný 5-dňový audit
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.1] mb-3">
            Tu je tvoj audit recap.
          </h1>
          <p className="text-white/60 text-base sm:text-lg">
            {data.scan_count} skenov · {data.is_owner_view ? "Tvoj vlastný recap" : "Zdieľaný recap"} ·
            <span className="text-white/40 ml-1 font-mono">{data.owner_email_hint}</span>
          </p>
        </div>

        {/* Recommendation card */}
        {rec.scores && (
          <div className="bg-gradient-to-br from-emerald-500/15 to-amber-500/5 border border-emerald-500/30 rounded-2xl p-6 mb-8" data-testid="recap-recommendation">
            <div className="text-[10px] text-emerald-300 font-mono uppercase tracking-widest mb-2">Odporúčaný bundle</div>
            <h2 className="text-2xl sm:text-3xl font-black mb-4">{BUNDLE_TITLE[dominantBundle] || "Bundle"}</h2>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(scores).map(([dim, score]) => {
                const meta = CATEGORY[dim];
                const isDominant = dim === dominantBundle;
                return (
                  <div
                    key={dim}
                    className={`rounded-lg border p-3 ${isDominant ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-white/5"}`}
                    data-testid={`recap-score-${dim}`}
                  >
                    {meta && <meta.Icon className={`w-4 h-4 mb-1.5 ${meta.color}`}/>}
                    <div className="text-[10px] text-white/50 font-mono uppercase tracking-wider mb-0.5">{dim}</div>
                    <div className={`text-2xl font-black ${isDominant ? "text-emerald-300" : "text-white/80"}`}>
                      {Math.round(score)}<span className="text-xs text-white/40">/100</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Scans list */}
        <div className="space-y-3 mb-10" data-testid="recap-scans">
          <h2 className="text-xl font-black mb-3 text-white/85">Tvojich {data.scan_count} skenov</h2>
          {data.scans.length === 0 && (
            <p className="text-white/50 text-sm">Žiadne uložené skeny pre tento účet.</p>
          )}
          {data.scans.map((s, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <a href={s.url} target="_blank" rel="noreferrer" className="text-sm text-emerald-400 hover:text-emerald-300 font-mono truncate">{s.url}</a>
                <span className="text-[10px] text-white/40 font-mono whitespace-nowrap">{(s.created_at || "").slice(0, 10)}</span>
              </div>
              {s.scores && (
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(s.scores).map(([dim, sc]) => (
                    <span key={dim} className="px-2 py-1 bg-black/40 border border-white/10 rounded text-xs font-mono">
                      <span className="text-white/40">{dim[0].toUpperCase()}</span>
                      <span className="text-white/85 ml-1 font-bold">{sc}</span>
                    </span>
                  ))}
                </div>
              )}
              {s.prescribed_bundle && (
                <div className="mt-2 text-[10px] text-amber-300 font-mono uppercase tracking-wider">
                  Odporúčaný bundle: {s.prescribed_bundle}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Ambassador progress — owner only */}
        {data.is_owner_view && data.ambassador && (
          <AmbassadorBar email={data.owner_email_hint.replace("***", "")} amb={data.ambassador} ownerEmail={data._ownerEmailFromUrl || ""}/>
        )}

        {/* Share row */}
        <div className="border border-white/10 rounded-2xl p-5 mb-10" data-testid="recap-share">
          <h3 className="text-base font-black mb-2 inline-flex items-center gap-2">
            <Share2 className="w-4 h-4 text-emerald-400"/> Zdieľaj s tímom
          </h3>
          <p className="text-sm text-white/60 mb-4">
            Tento link funguje pre kohokoľvek — kolegovia ho otvoria bez prihlásenia.
            {data.share_views > 0 && (
              <span className="ml-2 text-emerald-300 font-mono text-xs">
                ({data.share_views} {data.share_views === 1 ? "zdieľané otvorenie" : "zdieľaných otvorení"})
              </span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={typeof window !== "undefined" ? window.location.href : ""}
              className="flex-1 px-3 py-2 bg-black/40 border border-white/15 rounded text-xs text-white/70 font-mono"
              data-testid="recap-share-input"
            />
            <button
              onClick={copyLink}
              data-testid="recap-share-copy"
              className="px-3 py-2 bg-white text-black font-bold text-xs rounded hover:bg-emerald-400 transition-colors inline-flex items-center gap-1.5"
            >
              {copied ? <><CheckCircle2 className="w-3.5 h-3.5"/> Skopírované</> : <><Copy className="w-3.5 h-3.5"/> Kopírovať</>}
            </button>
          </div>
        </div>

        {/* Viral CTA — shown stronger to non-owners */}
        <div
          className="bg-gradient-to-br from-emerald-500/15 to-transparent border border-emerald-500/30 rounded-2xl p-6 sm:p-8 text-center"
          data-testid="recap-viral-cta"
        >
          {!data.is_owner_view ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
                Páči sa ti to?
              </div>
              <h2 className="text-2xl sm:text-3xl font-black mb-2">Chceš vlastný 5-dňový audit?</h2>
              <p className="text-white/60 text-base mb-5 max-w-xl mx-auto">
                Tvoj kolega ti to nepošle náhodou. Tu je presne to isté — 5 dní, 5 úloh, 100 % zdarma.
              </p>
              <Link
                to="/vyzva-5-dni"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black text-base sm:text-lg transition-colors"
              >
                Rezervovať miesto zdarma <ArrowRight className="w-5 h-5"/>
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-xl sm:text-2xl font-black mb-2">Ešte si neaktivoval bundle?</h2>
              <p className="text-white/60 text-sm mb-5">
                Promo cena DIAGNOSTIKA50 sa znova otvorí so štartom najbližšej kohorty.
              </p>
              <Link
                to="/vyzva-5-dni"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-sm transition-colors"
              >
                Pozrieť ďalšiu kohortu <ArrowRight className="w-4 h-4"/>
              </Link>
            </>
          )}
        </div>
      </div>

      <footer className="border-t border-white/5 px-6 py-8 text-center text-white/30 text-xs font-mono">
        © TopRankPulse · 5-Day Audit Recap · zdieľateľný link
      </footer>
    </div>
  );
}

function AmbassadorBar({ amb }) {
  const tiers = amb.all_tiers || [];
  const current = amb.current_tier;
  const next = amb.next_tier;
  const progressPct = next
    ? Math.min(100, Math.round((amb.share_views / next.share_views) * 100))
    : 100;

  return (
    <div
      className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/30 rounded-2xl p-5 sm:p-6 mb-8"
      data-testid="recap-ambassador"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] text-amber-300 font-mono uppercase tracking-widest mb-1">
            Tichý ambasádorský program
          </div>
          <h3 className="text-xl font-black">
            {current ? `${current.label} ambasádor` : "Začni zdieľať a získaj odmeny"}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black text-amber-300 tabular-nums">{amb.share_views}</div>
          <div className="text-[10px] text-white/40 font-mono uppercase">share views</div>
        </div>
      </div>

      {next && (
        <>
          <div className="h-2 bg-black/40 rounded-full overflow-hidden mb-2">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-emerald-400 transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="text-xs text-white/60 mb-4">
            Do <span className="text-amber-300 font-bold">{next.label}</span>
            {" "}({next.reward}): potrebuješ {Math.max(0, next.share_views - amb.share_views)} ďalších zdieľaní
            {next.conversions > 0 && ` + ${Math.max(0, next.conversions - amb.conversions)} konverzií`}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiers.map((t) => {
          const earned = amb.share_views >= t.share_views && amb.conversions >= t.conversions;
          return (
            <div
              key={t.id}
              className={`rounded-lg border p-2.5 ${earned ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-white/[0.02]"}`}
              data-testid={`tier-${t.id}`}
            >
              <div className={`text-[10px] font-mono uppercase tracking-wider mb-1 ${earned ? "text-emerald-300" : "text-white/40"}`}>
                {earned ? "✓ Uzamknuté" : t.label}
              </div>
              <div className={`text-xs font-bold ${earned ? "text-emerald-200" : "text-white/60"}`}>
                {t.share_views} views{t.conversions > 0 && ` + ${t.conversions} konv.`}
              </div>
              <div className="text-[10px] text-white/40 mt-1 leading-tight">{t.reward}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
